import type { Recipe } from '../types';

// Southern-style buttermilk cornbread, scaled up from a 10-inch skillet to a
// 9x13 so one bake feeds a chili night and stocks the freezer. Mostly cornmeal,
// a little flour so squares hold together after freezing, barely any sugar.
// The hot buttered pan is the whole trick.

export const southernSkilletCornbread: Recipe = {
  slug: 'southern-skillet-cornbread',
  title: 'Southern Buttermilk Cornbread',
  meal: 'dinner',
  category: 'side',
  types: ['bread'],
  cuisines: ['american'],
  traits: ['freezer-friendly', 'serve-with', 'make-ahead'],
  yield: '15 pieces — 15 servings',
  activeTime: '10 min',
  totalTime: '40 min',
  freezerLife: '3 months',
  difficulty: 1,

  summary:
    'Two bowls, one whisk, no mixer. Butter melts in the pan while the oven heats, the batter hits it with a sizzle, and 22 minutes later you have a 9x13 of golden cornbread with crispy edges. Built to sit next to Grandpa\'s chili.',

  flavorNotes:
    'Savory, tangy from the buttermilk, deep corn flavor, with a crunchy brown crust on the bottom and edges and a tender, slightly crumbly middle. This is not the cake-like sweet kind from a box — just a whisper of sugar to help the browning. If you want it sweeter, drizzle honey at the table rather than adding sugar to the batter. If yours came out dry, it baked too long; pull it as soon as the toothpick comes out clean.',

  ingredients: [
    {
      id: 'butter',
      name: 'Unsalted butter',
      total: '10 tbsp',
      prep: '4 tbsp goes in the pan whole, 6 tbsp melted for the batter',
      shop: 'Unsalted butter, 1 lb (4 sticks)',
      uses: [
        { step: 1, amount: '4 tbsp', note: 'in the pan, to sizzle' },
        { step: 3, amount: '6 tbsp', note: 'melted, into the batter' },
      ],
    },
    {
      id: 'cornmeal',
      name: 'Yellow cornmeal',
      total: '3 cups',
      prep: 'plain cornmeal — NOT self-rising mix. Stone-ground if you can find it',
      shop: 'Indian Head Stone Ground Yellow Corn Meal, 32 oz (plain, NOT self-rising mix)',
      uses: [{ step: 2, amount: '3 cups' }],
    },
    {
      id: 'ap-flour',
      name: 'All-purpose flour',
      total: '1 cup',
      shop: 'Great Value all-purpose flour, 5 lb bag',
      uses: [{ step: 2, amount: '1 cup' }],
    },
    {
      id: 'sugar',
      name: 'Sugar',
      total: '2 tbsp',
      uses: [{ step: 2, amount: '2 tbsp' }],
    },
    {
      id: 'baking-powder',
      name: 'Baking powder',
      total: '1 tbsp',
      uses: [{ step: 2, amount: '1 tbsp' }],
    },
    {
      id: 'baking-soda',
      name: 'Baking soda',
      total: '1 tsp',
      uses: [{ step: 2, amount: '1 tsp' }],
    },
    {
      id: 'salt',
      name: 'Salt',
      total: '1½ tsp',
      uses: [{ step: 2, amount: '1½ tsp' }],
    },
    {
      id: 'buttermilk',
      name: 'Whole buttermilk',
      total: '3 cups',
      prep: 'full-fat cultured; check the label for gums or carrageenan',
      shop: 'Whole cultured buttermilk, 1 quart',
      uses: [{ step: 3, amount: '3 cups' }],
    },
    {
      id: 'eggs',
      name: 'Large eggs',
      total: '3',
      uses: [{ step: 3, amount: '3' }],
    },
    {
      id: 'honey',
      name: 'Honey',
      total: 'for drizzling',
      optional: true,
      optionalKind: 'garnish',
      optionalWhy:
        'Goes on at the table for anyone who wants it sweet. Keeps the pan itself savory for chili.',
      shop: false,
      uses: [{ step: 5, amount: 'a drizzle', note: 'at the table' }],
    },
  ],

  steps: [
    {
      n: 1,
      title: 'Butter in the pan, pan in the oven',
      time: '10 min',
      critical: true,
      ingredientIds: ['butter'],
      body:
        'Put {4 tbsp|butter} of butter in a metal 9x13 pan and set it in the oven, then heat the oven to 425°F with the pan inside. Mix the batter while it heats. You want the butter melted, foaming and just starting to turn light brown when the batter goes in — a preheated pan is what makes the crispy edges.',
      tip:
        'Metal pan only. A glass Pyrex dish can crack when it goes into a 425°F oven empty and then gets cold batter poured in. Watch the butter after about 8 minutes — light brown is perfect, black means start over.',
    },
    {
      n: 2,
      title: 'Whisk the dry',
      time: '2 min',
      ingredientIds: ['cornmeal', 'ap-flour', 'sugar', 'baking-powder', 'baking-soda', 'salt'],
      body:
        'In a big bowl whisk {3 cups|cornmeal} of cornmeal, {1 cup|ap-flour} of flour, {2 tbsp|sugar} of sugar, {1 tbsp|baking-powder} of baking powder, {1 tsp|baking-soda} of baking soda and {1½ tsp|salt} of salt. Use plain cornmeal — if the bag says self-rising, it already has salt and leavening in it and the cornbread will come out salty and bitter.',
    },
    {
      n: 3,
      title: 'Wet in, stir just until mixed',
      time: '3 min',
      critical: true,
      ingredientIds: ['buttermilk', 'eggs', 'butter'],
      body:
        'In a second bowl whisk {3 cups|buttermilk} of buttermilk with {3|eggs} eggs. Melt {6 tbsp|butter} of butter in the microwave and let it cool a minute, then whisk it in. Pour the wet into the dry and stir with a spatula only until no dry streaks are left. Lumpy batter is correct — stop there.',
      tip:
        'Overmixing makes cornbread tough and full of tunnels. Ten or twelve folds is plenty. Let the batter sit while the pan finishes heating; the cornmeal soaks up liquid and the bread comes out more tender.',
    },
    {
      n: 4,
      title: 'Pour into the hot pan and bake',
      time: '22 min',
      critical: true,
      ingredientIds: [],
      body:
        'Pull the hot pan out with oven mitts, tilt it so the butter coats the bottom and up the sides, and pour the batter straight in — it should sizzle on contact. Spread it level and bake 20 to 25 minutes, until the top is golden, the edges are deep brown and pulling away from the pan, and a toothpick in the center comes out clean.',
      tip:
        'No sizzle means the pan was not hot enough. It will still taste good, but the edges will be soft instead of crunchy.',
    },
    {
      n: 5,
      title: 'Rest, then cut',
      time: '10 min',
      ingredientIds: ['honey'],
      body:
        'Let it cool in the pan 10 minutes so it sets, then cut into 15 squares (3 rows by 5). Serve warm with butter, and honey at the table for anyone who wants it sweet.',
    },
  ],

  freezing:
    'Cool completely on a rack. Wrap squares individually in plastic wrap or foil, then into a gallon freezer bag with the air pressed out. Up to 3 months. Wrapping each piece keeps them from drying out and lets you pull just two for a bowl of chili.',

  reheat: [
    {
      method: 'Oven or toaster oven, from frozen',
      best: true,
      detail:
        'Unwrap, 350°F for 10 to 12 minutes. The edges crisp back up and the middle goes soft and warm — closest to fresh.',
    },
    {
      method: 'Microwave',
      detail:
        'Unwrapped, wrapped in a damp paper towel, 30 to 40 seconds from frozen. Fast and soft, but no crunchy edges.',
    },
    {
      method: 'Skillet',
      detail:
        'Thawed, split in half, a little butter in a pan over medium, 2 minutes per side. Toasty and great for breakfast.',
    },
  ],

  glossary: [
    {
      term: 'preheated pan',
      plain:
        'Heating the empty pan (with the fat in it) in the oven before the batter goes in. The batter starts cooking the second it hits the hot metal, which fries the outside into a crispy brown crust. This is the main thing that separates Southern cornbread from the soft kind.',
    },
    {
      term: 'self-rising',
      plain:
        'Cornmeal or flour that already has baking powder and salt mixed into the bag. Handy in some recipes, but this one adds its own, so using self-rising doubles them up — salty, bitter and over-risen.',
    },
    {
      term: 'buttermilk',
      plain:
        'Tangy, thick cultured milk. The acid in it reacts with the baking soda to make the bread rise, and gives the classic tangy flavor. No buttermilk? Stir 3 tablespoons of lemon juice or white vinegar into 3 cups of whole milk and wait 5 minutes — close enough.',
    },
    {
      term: 'Lumpy batter',
      plain:
        'Batter stirred only until the flour disappears, with small lumps left in. Stirring more builds gluten, which makes quick breads like cornbread chewy and tough instead of tender.',
    },
    {
      term: 'Stone-ground',
      plain:
        'Cornmeal ground slowly between stones, keeping more of the corn\'s germ and bran. More corn flavor and a little more texture than regular fine cornmeal. Either works here.',
    },
  ],

  inspiration: [
    { label: 'Southern Plate — Deep South buttermilk cornbread', url: 'https://www.southernplate.com/wprm_print/deep-south-buttermilk-cornbread' },
    { label: 'Rose Bakes — Grandma\'s Southern buttermilk cornbread', url: 'https://rosebakes.com/southern-buttermilk-cornbread/' },
    { label: 'Food Network — Southern cornbread', url: 'https://www.foodnetwork.com/recipes/southern-cornbread-2012267.amp' },
  ],

  whereWeVeered:
    'Traditional Southern cornbread is all cornmeal, no sugar, baked in a 10-inch cast-iron skillet with bacon grease. We scaled it to a 9x13 so it makes 15 pieces for the freezer, used butter instead of bacon grease, and added one cup of flour so the squares stay in one piece after freezing and reheating. Kept the sugar to 2 tablespoons — enough to help the crust brown, not enough to make it sweet. Honey goes on at the table instead.',
};
